import type { LocalDate } from '@js-joda/core';
import type { CorporateAction, CorporateActionType } from '@thinkalpha/quant-ws-client/contracts/corporate-actions.js';
import { EVENTS_FUTURE_HORIZON_DAYS } from '@thinkalpha/quant-ws-client/contracts/time-list-page.js';
import type {
    EventsPageRequest,
    EventsPageResponse,
    EventsPosition,
} from '@thinkalpha/quant-ws-client/contracts/time-list-page.js';
import { createPositionPageAdapter } from '@thinkalpha/ui-symbol-tracked-time-list';
import type { SymbolTrackedTimeList, TimeListAdapter, TimeListConfig } from '@thinkalpha/ui-symbol-tracked-time-list';
import type { EventType } from 'src/components/ui/EventList/viewModel';

export type EventsQuery = EventsPageRequest['query'];
export type EventsTimeList = SymbolTrackedTimeList<
    CorporateAction,
    EventsQuery,
    EventsPageRequest,
    EventsPageResponse,
    EventsPosition
>;

export const EVENT_ACTION_TYPES = {
    ipo: 'NewListing',
    'symbol-change': 'SymbolChange',
    'primary-exchange-change': 'PrimaryExchangeChange',
    earnings: 'Earnings',
    dividend: 'Dividend',
    split: 'Split',
    'shares-outstanding-change': 'SharesOutstandingChange',
    acquisition: 'Acquisition',
    buyback: 'StockBuyback',
} satisfies Record<EventType, CorporateActionType>;

/** The existing menu labels an empty selection "All Events"; the wire represents that as null. */
export function eventsQuery(anchor: LocalDate | null, types: readonly EventType[]): EventsQuery {
    return {
        anchor,
        types: types.length ? types.map((type) => EVENT_ACTION_TYPES[type]) : null,
        windowEnd: 'server-today',
        futureTypes: ['Earnings', 'Split'],
        futureHorizonDays: EVENTS_FUTURE_HORIZON_DAYS,
    } satisfies EventsQuery;
}

export const EVENTS_TIME_LIST_CONFIG: TimeListConfig = {
    pageSize: 79,
    retainedWindowRows: 300,
    overscanRows: 20,
    rowHeight: 24,
    separatorHeight: 24,
    bufferHeight: 120,
    pinnedTolerance: 0,
    liveInterval: 10_000,
    leadInterval: 10_000,
};

export function createEventsAdapter(): TimeListAdapter<
    CorporateAction,
    EventsQuery,
    EventsPageRequest,
    EventsPageResponse,
    EventsPosition
> {
    const adapter = createPositionPageAdapter<CorporateAction, EventsQuery, EventsPosition>({
        keyOf: (row) => row.id,
        // Corporate actions use canonical hexadecimal ObjectId strings; lexical order equals byte order.
        compare: (a, b) => b.exDate.compareTo(a.exDate) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0),
        cursorOf: ({ exDate, id }) => ({ exDate, id }),
        symbolsOf: (row) => [row.symbol],
        calendarDateOf: (row) => row.exDate,
        declaresLeadSet: true,
    });
    return {
        ...adapter,
        toRequest(instruction) {
            return { ...adapter.toRequest(instruction), symbols: [...instruction.symbols] } satisfies EventsPageRequest;
        },
    };
}
